import React from 'react'
import { Link } from 'react-router-dom'
import { useStructuraAuth } from '@structura/auth'
import { useTranslation } from '@structura/i18n'

export interface HomePageProps {
  // Per-app dashboard rendered for authenticated visitors. Each app passes its
  // own Dashboard component (intentionally not shared — content differs per
  // app, see the per-app `Dashboard.tsx`).
  dashboard: React.ReactNode
}

// `/` is the single canonical landing. Anonymous visitors get the marketing
// hero; authenticated visitors see the per-app Dashboard rendered inline. The
// old /dashboard route still 301s here so any stale bookmark resolves.
const Home: React.FC<HomePageProps> = ({ dashboard }) => {
  const authState = useStructuraAuth()
  const { t } = useTranslation()

  const handleLogin = React.useCallback(async (): Promise<void> => {
    window.location.href = '/account'
  }, [])

  if (authState.isAuthenticated) {
    return <>{dashboard}</>
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="display-4 fw-semibold mb-4 text-dark">
                {t('pages.home.heroTitle')}
              </h1>
              <p className="lead mb-3">
                {t('pages.home.heroSubtitle')}
              </p>
              <div className="d-flex flex-wrap gap-3">
                <Link to="/register" className="btn btn-register-primary btn-lg">
                  <i className="bi bi-heart me-2"></i>
                  {t('pages.home.register')}
                </Link>
                <button
                  type="button"
                  className="btn btn-register-primary btn-lg"
                  onClick={handleLogin}
                  disabled={!authState.isSupported || authState.isLoading}
                >
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  {t('pages.home.signIn')}
                </button>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="text-center">
                <img
                  src="/logo.svg"
                  alt={t('pages.logoAlt')}
                  style={{width: '300px', height: '300px', opacity: 0.9}}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <h3 className="fw-semibold text-dark mb-4">{t('pages.home.whyChoose')}</h3>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('pages.home.multiSpanLabel')}</strong> {t('pages.home.multiSpanText')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('pages.home.eurocodeLabel')}</strong> {t('pages.home.eurocodeText')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('pages.home.interactiveLabel')}</strong> {t('pages.home.interactiveText')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-check-circle-fill home-icon me-2"></i>
                  <strong>{t('pages.home.professionalLabel')}</strong> {t('pages.home.professionalText')}
                </li>
              </ul>
            </div>
            <div className="col-lg-6">
              <h3 className="fw-semibold text-dark mb-4">{t('pages.home.technicalFeatures')}</h3>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <i className="bi bi-gear-fill home-icon me-2"></i>
                  {t('pages.home.feat1')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-graph-up home-icon me-2"></i>
                  {t('pages.home.feat2')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-diagram-2-fill home-icon me-2"></i>
                  {t('pages.home.feat3')}
                </li>
                <li className="mb-3">
                  <i className="bi bi-file-earmark-text home-icon me-2"></i>
                  {t('pages.home.feat4')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA section removed per request */}
    </div>
  )
}

export default Home 